import { X, AlertCircle, Plus, Trash2, Copy } from 'lucide-react';
import { useState, useEffect } from 'react';
import Card from '../ui/Card';
import PlayerSelect from '../forms/PlayerSelect';

const createEmptyMatch = () => ({
  t1p1: '',
  t1p2: '',
  t2p1: '',
  t2p2: '',
  score1: '',
  score2: '',
  matchTime: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16),
});

const MatchRecordModal = ({
  show,
  onSubmit,
  onClose,
  formError,
  players,
  onQuickAddPlayer,
}) => {
  const [matches, setMatches] = useState([createEmptyMatch()]);
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (matchIndex, name, value) => {
    setMatches(prev =>
      prev.map((match, idx) =>
        idx === matchIndex ? { ...match, [name]: value } : match
      )
    );
  };

  const isMatchValid = match => {
    return (
      match.t1p1 &&
      match.t1p2 &&
      match.t2p1 &&
      match.t2p2 &&
      match.score1 !== '' &&
      match.score2 !== '' &&
      match.t1p1 !== match.t1p2 &&
      match.t2p1 !== match.t2p2 &&
      match.t1p1 !== match.t2p1 &&
      match.t1p1 !== match.t2p2 &&
      match.t1p2 !== match.t2p1 &&
      match.t1p2 !== match.t2p2
    );
  };

  const isFormValid = () => {
    return matches.length > 0 && matches.every(isMatchValid);
  };

  const addMatch = () => {
    setMatches(prev => [...prev, createEmptyMatch()]);
  };

  const removeMatch = index => {
    if (matches.length > 1) {
      setMatches(prev => prev.filter((_, idx) => idx !== index));
    }
  };

  // Copy players from previous match
  const copyFromPreviousMatch = (matchIndex, copyType) => {
    if (matchIndex === 0) return; // No previous match

    const prevMatch = matches[matchIndex - 1];
    setMatches(prev =>
      prev.map((match, idx) => {
        if (idx !== matchIndex) return match;

        switch (copyType) {
          case 'all':
            return {
              ...match,
              t1p1: prevMatch.t1p1,
              t1p2: prevMatch.t1p2,
              t2p1: prevMatch.t2p1,
              t2p2: prevMatch.t2p2,
            };
          case 'black':
            return {
              ...match,
              t1p1: prevMatch.t1p1,
              t1p2: prevMatch.t1p2,
            };
          case 'white':
            return {
              ...match,
              t2p1: prevMatch.t2p1,
              t2p2: prevMatch.t2p2,
            };
          default:
            return match;
        }
      })
    );
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setMatches([createEmptyMatch()]);
    }
  }, [show]);

  const handleFormSubmit = async e => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(e, matches);
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/20 backdrop-blur-sm animate-in fade-in">
      <Card className="w-full max-w-xl shadow-2xl bg-white relative overflow-hidden max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-zinc-100 flex justify-between items-center bg-zinc-50/50">
          <h3 className="font-bold text-lg tracking-tight">
            Record Match{matches.length > 1 ? 'es' : ''}
            {matches.length > 1 && (
              <span className="ml-2 text-sm font-normal text-zinc-500">
                ({matches.length} matches)
              </span>
            )}
          </h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-zinc-400 hover:text-zinc-900" />
          </button>
        </div>

        <form onSubmit={handleFormSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-6 overflow-y-auto flex-1">
            {formError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded flex items-start gap-3 text-red-600 text-sm">
                <AlertCircle className="w-5 h-5 shrink-0" />
                {formError}
              </div>
            )}

            {matches.map((match, matchIndex) => (
              <div
                key={matchIndex}
                className={`${matchIndex > 0 ? 'mt-6 pt-6 border-t border-zinc-200' : ''}`}
              >
                {/* Match header with controls */}
                <div className="flex justify-between items-center mb-4">
                  {matches.length > 1 ? (
                    <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">
                      Match {matchIndex + 1}
                    </span>
                  ) : (
                    <span></span>
                  )}

                  <div className="flex items-center gap-2">
                    {/* Copy from previous match buttons */}
                    {matchIndex > 0 && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => copyFromPreviousMatch(matchIndex, 'all')}
                          className="px-2 py-1 text-xs text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded transition-colors flex items-center gap-1"
                          title="Copy all players from previous match"
                        >
                          <Copy className="w-3 h-3" />
                          All
                        </button>
                        <button
                          type="button"
                          onClick={() => copyFromPreviousMatch(matchIndex, 'black')}
                          className="px-2 py-1 text-xs text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded transition-colors flex items-center gap-1"
                          title="Copy Black team from previous match"
                        >
                          <div className="w-2 h-2 bg-zinc-900 rounded-full"></div>
                        </button>
                        <button
                          type="button"
                          onClick={() => copyFromPreviousMatch(matchIndex, 'white')}
                          className="px-2 py-1 text-xs text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded transition-colors flex items-center gap-1"
                          title="Copy White team from previous match"
                        >
                          <div className="w-2 h-2 bg-white border-2 border-zinc-400 rounded-full"></div>
                        </button>
                      </div>
                    )}

                    {/* Remove match button */}
                    {matches.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeMatch(matchIndex)}
                        className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Remove match"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-8 relative mb-6">
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center text-[10px] font-black text-zinc-300 border border-zinc-200 z-10 shadow-sm">
                    VS
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-zinc-100">
                      <div className="w-3 h-3 bg-zinc-900 rounded-full shadow-sm"></div>
                      <label className="text-xs font-bold uppercase tracking-widest text-zinc-900">
                        Black
                      </label>
                    </div>
                    <PlayerSelect
                      name={`t1p1_${matchIndex}`}
                      label=""
                      players={players}
                      onAddNew={onQuickAddPlayer}
                      value={match.t1p1}
                      onChange={value => handleInputChange(matchIndex, 't1p1', value)}
                    />
                    <PlayerSelect
                      name={`t1p2_${matchIndex}`}
                      label=""
                      players={players}
                      onAddNew={onQuickAddPlayer}
                      value={match.t1p2}
                      onChange={value => handleInputChange(matchIndex, 't1p2', value)}
                    />
                    <input
                      name={`score1_${matchIndex}`}
                      type="number"
                      min="0"
                      max="10"
                      placeholder="-"
                      value={match.score1}
                      onChange={e =>
                        handleInputChange(matchIndex, 'score1', e.target.value)
                      }
                      className="w-full bg-zinc-50 border border-zinc-200 rounded px-3 py-3 text-center font-mono text-2xl font-bold focus:border-zinc-900 outline-none transition-colors placeholder:text-zinc-300"
                      required
                    />
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-zinc-100">
                      <div className="w-3 h-3 bg-white border-2 border-zinc-300 rounded-full shadow-sm"></div>
                      <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                        White
                      </label>
                    </div>
                    <PlayerSelect
                      name={`t2p1_${matchIndex}`}
                      label=""
                      players={players}
                      onAddNew={onQuickAddPlayer}
                      value={match.t2p1}
                      onChange={value => handleInputChange(matchIndex, 't2p1', value)}
                    />
                    <PlayerSelect
                      name={`t2p2_${matchIndex}`}
                      label=""
                      players={players}
                      onAddNew={onQuickAddPlayer}
                      value={match.t2p2}
                      onChange={value => handleInputChange(matchIndex, 't2p2', value)}
                    />
                    <input
                      name={`score2_${matchIndex}`}
                      type="number"
                      min="0"
                      max="10"
                      placeholder="-"
                      value={match.score2}
                      onChange={e =>
                        handleInputChange(matchIndex, 'score2', e.target.value)
                      }
                      className="w-full bg-zinc-50 border border-zinc-200 rounded px-3 py-3 text-center font-mono text-2xl font-bold focus:border-zinc-900 outline-none transition-colors placeholder:text-zinc-300"
                      required
                    />
                  </div>
                </div>

                {/* Match Time Input */}
                <div className="space-y-2">
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                    Match Time
                  </label>
                  <input
                    name={`matchTime_${matchIndex}`}
                    type="datetime-local"
                    value={match.matchTime}
                    onChange={e =>
                      handleInputChange(matchIndex, 'matchTime', e.target.value)
                    }
                    className="w-full bg-white border border-zinc-300 rounded px-3 py-2 text-sm font-mono focus:border-zinc-900 outline-none transition-colors"
                  />
                </div>
              </div>
            ))}

            {/* Add Another Match Button */}
            <button
              type="button"
              onClick={addMatch}
              className="mt-6 w-full py-3 border-2 border-dashed border-zinc-300 rounded text-zinc-500 hover:border-zinc-400 hover:text-zinc-600 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Add Another Match
            </button>
          </div>

          <div className="p-6 border-t border-zinc-100 bg-zinc-50/50">
            <button
              type="submit"
              disabled={!isFormValid() || submitting}
              className={`w-full py-4 rounded font-bold transition-all text-sm tracking-widest uppercase ${
                isFormValid() && !submitting
                  ? 'bg-zinc-900 text-white hover:bg-black shadow-lg hover:shadow-xl'
                  : 'bg-zinc-200 text-zinc-400 cursor-not-allowed shadow-none opacity-60'
              }`}
            >
              {submitting
                ? 'Submitting...'
                : `Submit ${matches.length > 1 ? `${matches.length} Results` : 'Result'}`}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default MatchRecordModal;
